import type { GlobalDialogType } from '../global-dialogs.store.types';

import { useGlobalDialogsStore } from '../global-dialogs.store';

/**
 * Hook for getting the active key of a specific global dialog type.
 *
 * This hook returns the key of the currently active dialog instance for a given dialog type.
 * Since only one dialog of each type can be active at a time, this hook provides a simple
 * way to get the key that identifies the active instance.
 *
 * @param dialogType - The dialog type to get the active key for
 *
 * @returns The active key for the dialog type, or null if no dialog of that type is active
 *
 * @example
 * **Basic usage** - Getting the active dialog key:
 * ```tsx
 * const activeCampaignKey = useGlobalDialogKey(GlobalDialogType.CampaignPhoneReachOut);
 *
 * if (activeCampaignKey) {
 *   console.log(`Active campaign dialog key: ${activeCampaignKey}`);
 *   // Output: "Active campaign dialog key: campaign-123"
 * }
 * ```
 *
 * @example
 * **Using with other hooks** - Combining with data access:
 * ```tsx
 * const DialogContent = () => {
 *   const activeKey = useGlobalDialogKey(GlobalDialogType.CandidateOutreach);
 *   const dialogData = useGlobalDialogData({
 *     dialogType: GlobalDialogType.CandidateOutreach,
 *     key: activeKey
 *   });
 *
 *   if (!activeKey || !dialogData) {
 *     return <div>No active dialog</div>;
 *   }
 *
 *   return <div>Dialog for key: {activeKey}</div>;
 * };
 * ```
 *
 * @example
 * **Conditional rendering** - Showing different content based on active dialog:
 * ```tsx
 * const MyComponent = () => {
 *   const activeEmailKey = useGlobalDialogKey(GlobalDialogType.ShouldConnectEmail);
 *   const activeCampaignKey = useGlobalDialogKey(GlobalDialogType.CampaignPhoneReachOut);
 *
 *   if (activeEmailKey) {
 *     return <EmailConnectDialog />;
 *   }
 *
 *   if (activeCampaignKey) {
 *     return <CampaignDialog campaignId={activeCampaignKey} />;
 *   }
 *
 *   return <div>No active dialogs</div>;
 * };
 * ```
 *
 * @remarks
 * **Key Types**:
 * - For campaign/search dialogs: typically the campaign or search ID
 * - For page-specific dialogs: derived from page context (e.g., "inbox", "candidate-123")
 * - For simple dialogs: may be a generic key based on page context
 *
 * **Usage Notes**:
 * - Returns null when no dialog of the specified type is currently active
 * - Only one dialog of each type can be active at a time
 * - The key is automatically managed by the global dialogs system
 * - Keys are resolved using page context when dialogs are opened
 *
 * @see {@link useGlobalDialog} for opening and closing dialogs
 * @see {@link useGlobalDialogData} for accessing dialog data using keys
 * @see {@link useIsGlobalDialogOpen} for checking if a dialog is open
 */
export const useGlobalDialogKey = (dialogType: GlobalDialogType) => {
  return useGlobalDialogsStore((state) => state.activeDialogs.find((d) => d.type === dialogType)?.key || null);
};
